import React, { useState, useEffect } from "react";
import { CircularProgress } from "@material-ui/core";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import FormControl from "@material-ui/core/FormControl";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import FormLabel from "@material-ui/core/FormLabel";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import AddIcon from "@material-ui/icons/Add";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import Link from "@material-ui/core/Link";
import { ATHENA_VIR_PREPARE_THERMEON } from "src/types/actions";
import * as virActions from "src/actions/virActions";
import * as appActions from "src/actions/appActions";
import { useIsMount } from "src/hooks/useIsMount";
import useStyles from "src/layouts/DigitalVIRLayout/useStyles";
import { Button, RoundedButton } from "@packages/ui";
import getBrandFromUrl from "src/utils/getBrandFromUrl";
import VirPhoneInput from "src/components/VirPhoneInput/VirPhoneInput";
import { getEmailValidation, renderError } from "src/utils/emailValidation";
import { emit } from "process";
import DiveButton from "src/components/DiveButton/DiveButton";
import brandColors from "@packages/ui/theme/colors";


const DigitalVIRWitnessDetails = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [witnessDetails, setWitnessDetails] = useState(null);

  const athenaVirDetailPatch = useSelector((state) => state.vir.athenaVirDetailPatch);
  const athenaVirDetail = useSelector((state) => state.vir.athenaVirDetail);

  const selectedVirid = useSelector((state) => state.vir?.athenaSelectedId?.selectedId);
  const resubmitFlag = useSelector((state) => state.vir?.athenaVirResubmit?.flag);
  const athenaRole = useSelector((state) => state.vir?.athenaRole?.role);
  const disableEdit = resubmitFlag && athenaRole !== "staff";
  const isMount = useIsMount();
  const [loading, setLoading] = useState(false);
  const brand = getBrandFromUrl().toLowerCase();

  const previousData = resubmitFlag ? athenaVirDetail?.data : athenaVirDetailPatch?.data ?? {};
  const [driverLicence, setDriverLicence] = useState([
    previousData.Witness1?.DriverLicenceUploaded,
    previousData.Witness2?.DriverLicenceUploaded,
  ]);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      witnesses: previousData.Witnesses ? "yes" : "no",
      firstName: previousData.Witness1?.FirstName ? previousData.Witness1?.FirstName : "",
      lastName: previousData.Witness1?.LastName ? previousData.Witness1?.LastName : "",
      cellTelephoneNumber: previousData.Witness1?.Mobile ? previousData.Witness1?.Mobile : "",
      email: previousData.Witness1?.Email ? previousData.Witness1?.Email : "",

      additionalWitnesses: previousData.Witness2
        ? [
            {
              firstName: previousData.Witness2?.FirstName ? previousData.Witness2?.FirstName : "",
              lastName: previousData.Witness2?.LastName ? previousData.Witness2?.LastName : "",
              cellTelephoneNumber: previousData.Witness2?.Mobile
                ? previousData.Witness2?.Mobile
                : "",
              email: previousData.Witness2?.Email ? previousData.Witness2?.Email : "",
            },
          ]
        : [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalWitnesses",
  });

  // Watch form values for DiveButton
  const formFirstName = watch("firstName");
  const formLastName = watch("lastName");
  const formPhone = watch("cellTelephoneNumber");
  const formEmail = watch("email");

  // Watch additional witnesses fields for DiveButton
  const additionalWitnessesValues = watch("additionalWitnesses");

  const handleDiveVerificationComplete = (success, driverIndex = 0) => {
    if (success) {
      const newDriverLicence = [...driverLicence];
      newDriverLicence[driverIndex] = true;
      setDriverLicence(newDriverLicence);
      dispatch(
        appActions.createToast({
          type: "success",
          message: "Driver license verification completed successfully",
        })
      );
    }
  };

  const handleTextFieldChange = (event) => {
    const { name, value } = event.target;
    setValue(name, value);
  };
  useEffect(() => {
    if (!isMount) {
      if (!athenaVirDetailPatch.loading && loading) {
        setLoading(false);
        if (athenaVirDetailPatch.error) {
          dispatch(
            appActions.createToast({
              type: "error",
              message: "Failed to update",
            })
          );
        } else {
          dispatch(
            appActions.createToast({
              type: "success",
              message: "Vehicle Incident Report is updated",
            })
          );
          dispatch(
            virActions.detailVir({
              virId: selectedVirid,
            })
          );
          navigate(`/vehicleincidents/damagedetails?brand=${brand}`);
        }
      }
    }
  }, [athenaVirDetailPatch.loading]);

  const handlePhoneChange = (value, name) => {
    setValue(name, value);
  };

  const onSubmit = (data) => {
    setLoading(true);
    const virPayloadTHERMEON = {};

    virPayloadTHERMEON["Witnesses"] = data.witnesses;
    if (data.witnesses === "yes") {
      virPayloadTHERMEON["Witnesses1"] = {
        FirstName: data.firstName,
        LastName: data.lastName,
        CellTelephoneNumber: data.cellTelephoneNumber,
        Email: data.email,
      };
    }
    const witnessData =
      data.witnesses === "yes"
        ? {
            Witnesses: data.witnesses,
            Witness1: {
              FirstName: data.firstName,
              LastName: data.lastName,
              Mobile: data.cellTelephoneNumber,
              Email: data.email,
              DriverLicenceUploaded: driverLicence[0],
            },
          }
        : {
            Witnesses: data.witnesses,
          };

    const params = {
      virId: selectedVirid,
      virStatus: resubmitFlag ? "SubmissionPage" : "Witness",
      data: witnessData,
    };

    if (data.additionalWitnesses && data.additionalWitnesses.length > 0) {
      data.additionalWitnesses.forEach((witness, index) => {
        const witnessKey = `Witness${index + 2}`; // Witness2, Witness3
        params.data[witnessKey] = {
          FirstName: witness.firstName,
          LastName: witness.lastName,
          Mobile: witness.cellTelephoneNumber,
          Email: witness.email,
          DriverLicenceUploaded: driverLicence[index + 1],
        };
      });
      virPayloadTHERMEON["AdditionalWitnesses"] = data.additionalWitnesses.map((witness) => ({
        FirstName: witness.firstName,
        LastName: witness.lastName,
        CellTelephoneNumber: witness.cellTelephoneNumber,
        Email: witness.email,
      }));
    }
    setWitnessDetails(data);
    dispatch({ type: ATHENA_VIR_PREPARE_THERMEON, payload: virPayloadTHERMEON });
    dispatch(virActions.detailPatchVir(params));
  };

  const removeWitnesses = (index) => {
    remove(index);
  };

  const addAnotherWitnesses = () => {
    if (fields.length < 1) {
      append({
        firstName: "",
        lastName: "",
        cellTelephoneNumber: "",
        email: "",
      });
    }
  };

  return (
    <>
      <Box>
        <h2 className={classes.heading}>
          Please provide details of any witnesses of the incident.
        </h2>

        <br></br>
      </Box>
      <hr className={classes.breakLine} style={{ borderColor: brandColors[brand] }} />
      <Box
        sx={{
          p: 4,
          textAlign: "left",
          fontFamily: "Ride",
          color: "#333",
          maxWidth: "500px",
          margin: "0 auto",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl component="fieldset">
            <FormLabel component="legend" className={classes.radioFormat}>
              Were there any witnesses?
            </FormLabel>
            <Controller
              rules={{ required: true }}
              control={control}
              name="witnesses"
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel
                    value="yes"
                    disabled={
                      disableEdit &&
                      (previousData?.Witnesses !== null || previousData?.Witnesses !== undefined) &&
                      previousData?.Witnesses
                    }
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="no"
                    disabled={
                      disableEdit &&
                      (previousData?.Witnesses !== null || previousData?.Witnesses !== undefined) &&
                      previousData?.Witnesses
                    }
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              )}
            />

            {watch("witnesses") == "yes" && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Controller
                      control={control}
                      rules={{ required: "First Name is required" }}
                      name="firstName"
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          inputProps={{
                            autoComplete: "new-password",
                          }}
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          multiline
                          maxRows={1}
                          id="firstName"
                          name="firstName"
                          label="First Name"
                          placeholder="Enter First Name"
                          value={value}
                          error={errors.firstName ? true : false}
                          onChange={handleTextFieldChange}
                          helperText={errors.firstName?.message}
                          disabled={
                            disableEdit &&
                            previousData?.Witness1?.FirstName !== undefined &&
                            previousData?.Witness1?.FirstName !== ""
                          }
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      control={control}
                      rules={{ required: "Last Name is required" }}
                      name="lastName"
                      render={({ field: { onChange, value } }) => (
                        <TextField
                          inputProps={{
                            autoComplete: "new-password",
                          }}
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          multiline
                          maxRows={1}
                          id="lastName"
                          name="lastName"
                          label="Last Name"
                          placeholder="Enter Last Name"
                          value={value}
                          error={errors.lastName ? true : false}
                          onChange={handleTextFieldChange}
                          helperText={errors.lastName?.message}
                          disabled={
                            disableEdit &&
                            previousData?.Witness1?.LastName !== undefined &&
                            previousData?.Witness1?.LastName !== ""
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <DiveButton
                    disabled={driverLicence[0]}
                    firstName={formFirstName || ""}
                    lastName={formLastName || ""}
                    phone={formPhone || ""}
                    email={formEmail || ""}
                    virId={selectedVirid}
                    brand={brand}
                    onVerificationComplete={(success) => handleDiveVerificationComplete(success, 0)}
                    endpointName={"witness"}
                    onClickValidate={async () => {
                      return await trigger(["firstName", "lastName"]);
                    }}
                    title={
                      driverLicence[0]
                        ? "Witness 1's driver license is uploaded"
                        : "Upload Witness 1's driver license"
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Controller
                          control={control}
                          name="cellTelephoneNumber"
                          className={classes.flexContainer}
                          render={({ field: { value, onChange } }) => (
                            <VirPhoneInput
                              phoneNumber={value}
                              onChange={(phone) => handlePhoneChange(phone, "cellTelephoneNumber")}
                              disabled={
                                disableEdit &&
                                previousData?.Witness1?.Mobile !== undefined &&
                                previousData?.Witness1?.Mobile !== ""
                              }
                            />
                          )}
                        />
                      </Box>
                    </Grid>
                  </Box>

                  <TextField
                    label="Email Address"
                    variant="outlined"
                    fullWidth
                    margin="normal"
                    {...register("email", getEmailValidation())}
                    disabled={
                      disableEdit &&
                      previousData?.Witness1?.Email !== undefined &&
                      previousData?.Witness1?.Email !== ""
                    }
                  />
                  {renderError(errors, "email")}
                </Grid>

                <ul style={{ listStyle: "none", paddingLeft: 0 }}>
                  {fields.map((item, index) => {
                    return (
                      <li key={item.id}>
                        <Box mb={3}>
                          <FormLabel>Additional Witness {index + 1}</FormLabel>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              label="First Name"
                              variant="outlined"
                              fullWidth
                              error={!!errors?.additionalWitnesses?.[index]?.firstName}
                              helperText={errors?.additionalWitnesses?.[index]?.firstName?.message}
                              {...register(`additionalWitnesses.${index}.firstName`, {
                                required: "First Name is required",
                              })}
                              disabled={
                                disableEdit &&
                                previousData?.Witness2?.FirstName !== undefined &&
                                previousData?.Witness2?.FirstName !== ""
                              }
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              label="Last Name"
                              variant="outlined"
                              fullWidth
                              error={!!errors?.additionalWitnesses?.[index]?.lastName}
                              helperText={errors?.additionalWitnesses?.[index]?.lastName?.message}
                              {...register(`additionalWitnesses.${index}.lastName`, {
                                required: "Last Name is required",
                              })}
                              disabled={
                                disableEdit &&
                                previousData?.Witness2?.LastName !== undefined &&
                                previousData?.Witness2?.LastName !== ""
                              }
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <DiveButton
                              disabled={driverLicence[index + 1]}
                              firstName={additionalWitnessesValues?.[index]?.firstName || ""}
                              lastName={additionalWitnessesValues?.[index]?.lastName || ""}
                              phone={additionalWitnessesValues?.[index]?.cellTelephoneNumber || ""}
                              email={additionalWitnessesValues?.[index]?.email || ""}
                              virId={selectedVirid}
                              brand={brand}
                              onVerificationComplete={(success) => handleDiveVerificationComplete(success, index + 1)}
                              endpointName={`witness${index + 1}`}
                              onClickValidate={async () => {
                                return await trigger([
                                  `additionalWitnesses.${index}.firstName`,
                                  `additionalWitnesses.${index}.lastName`,
                                ]);
                              }}
                              title={
                                driverLicence[index + 1]
                                  ? `Witness ${index + 2}'s driver license is uploaded`
                                  : `Upload Witness ${index + 2}'s driver license`
                              }
                            />
                          </Grid>
                        </Grid>

                        <Grid item xs={12}>
                          <Box sx={{ mt: 2 }}>
                            <Grid item xs={12}>
                              <Box sx={{ mt: 2, mb: 2 }}>
                                <Controller
                                  control={control}
                                  name={`additionalWitnesses.${index}.cellTelephoneNumber`}
                                  className={classes.flexContainer}
                                  render={({ field: { value, onChange } }) => (
                                    <VirPhoneInput
                                      phoneNumber={value}
                                      onChange={(phone) =>
                                        handlePhoneChange(
                                          phone,
                                          `additionalWitnesses.${index}.cellTelephoneNumber`
                                        )
                                      }
                                      disabled={
                                        disableEdit &&
                                        previousData[`Witness${index + 2}`]?.Mobile !== undefined &&
                                        previousData[`Witness${index + 2}`]?.Mobile !== ""
                                      }
                                    />
                                  )}
                                />
                              </Box>
                            </Grid>
                          </Box>

                          <TextField
                            label="Email Address"
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            {...register(
                              `additionalWitnesses.${index}.email`,
                              getEmailValidation()
                            )}
                            disabled={
                              disableEdit &&
                              previousData?.Witness2?.Email !== undefined &&
                              previousData?.Witness2?.Email !== ""
                            }
                          />
                          {renderError(errors, `additionalWitnesses.${index}.email`)}
                        </Grid>
                        {fields.length < 2 && (
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => removeWitnesses(index)}
                          >
                            Remove additional witness
                          </Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {fields.length < 1 && (
                  <Link
                    onClick={addAnotherWitnesses}
                    underline="hover"
                    color="inherit"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    <Grid container>
                      <Grid item>
                        <IconButton size="small" edge="start">
                          <AddIcon />
                        </IconButton>
                      </Grid>
                      <Grid item xs={10}>
                        <Typography color="primary"> Add another witness.</Typography>
                      </Grid>
                    </Grid>
                  </Link>
                )}
              </>
            )}
          </FormControl>

          <Box sx={{ my: 3 }}>
            <RoundedButton
              variant="contained"
              fullWidth
              type="submit"
              sx={{ mt: 2, width: "80%", margin: "2" }}
              disabled={loading}
            >
              {loading ? <CircularProgress /> : "Continue"}
            </RoundedButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default DigitalVIRWitnessDetails;
